import { useState, type FormEvent } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import { serviceOptions, homeSizes } from '@/mocks/home';

const FORM_URL = 'https://readdy.ai/api/form/db8828bk13ra8mig8o00';
const inputCls = 'w-full h-[52px] rounded-xl border border-background-300 bg-background-50 px-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200';
type Status = 'idle' | 'sending' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [count, setCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const trap = String(data.get('company_alt') || '').trim();
    if (trap) { setStatus('success'); return; }
    data.delete('company_alt');

    const next: Record<string, string> = {};
    if (!String(data.get('full_name') || '').trim()) next.full_name = 'Please enter your name.';
    const email = String(data.get('email') || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid email address.';
    if (!data.get('service_type')) next.service_type = 'Please choose a service.';
    if (!data.get('home_size')) next.home_size = 'Please choose a property type.';
    if (String(data.get('details') || '').length > 500) next.details = 'Please keep details under 500 characters.';
    setErrors(next);
    if (Object.keys(next).length) {
      form.querySelector<HTMLElement>(`[name="${Object.keys(next)[0]}"]`)?.focus();
      return;
    }

    setStatus('sending');
    setFormError('');
    try {
      const body = new URLSearchParams();
      data.forEach((v, k) => { const val = String(v).trim(); if (val) body.append(k, val); });
      const response = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg || '')) {
        setStatus('success');
        form.reset();
        setCount(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  const field = (k: string) => ({ 'aria-invalid': Boolean(errors[k]), 'aria-describedby': errors[k] ? `h-${k}-err` : undefined, 'aria-required': true });
  const req = <span className="text-secondary-500" aria-hidden="true">*</span>;
  const err = (k: string) => errors[k] && <p id={`h-${k}-err`} role="alert" className="mt-1.5 text-xs text-foreground-950"><i className="ri-error-warning-line text-secondary-500 mr-1" />{errors[k]}</p>;

  return (
    <section id="quote" className="py-16 md:py-20 lg:py-[112px] px-4 md:px-6 lg:px-20 bg-background-200">
      <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow="LET'S GET STARTED" title="A Fresh Home Starts Here." sub="Tell us about your space, and we'll help you find the right cleaning service." />
          <ul className="mt-10 space-y-4">
            {['Share a few details about your home', 'Pick the service that fits', 'We follow up to plan the next steps'].map((t) => (
              <li key={t} className="flex items-center gap-3 text-[15px] text-foreground-900"><span className="w-7 h-7 rounded-full bg-background-50 text-secondary-500 flex items-center justify-center"><i className="ri-check-line" /></span>{t}</li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7">
          <form id="cleary-quote-form" data-readdy-form noValidate onSubmit={handleSubmit} className="quote-form relative rounded-[20px] bg-background-50 border border-background-300 p-6 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input type="text" name="company_alt" className="field-company-alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(ev) => ev.currentTarget.removeAttribute('readonly')} />
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium mb-2">Full Name {req}</label>
              <input id="full_name" name="full_name" className={inputCls} placeholder="Your name" autoComplete="name" required {...field('full_name')} />
              {err('full_name')}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">Email {req}</label>
              <input id="email" type="email" name="email" className={inputCls} placeholder="you@example.com" autoComplete="email" required {...field('email')} />
              {err('email')}
            </div>
            <div>
              <label htmlFor="service_type" className="block text-sm font-medium mb-2">Service Type {req}</label>
              <select id="service_type" name="service_type" defaultValue="" className={`${inputCls} cursor-pointer`} required {...field('service_type')}>
                <option value="" disabled>Select a service</option>
                {serviceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              {err('service_type')}
            </div>
            <div>
              <label htmlFor="home_size" className="block text-sm font-medium mb-2">Property Type / Home Size {req}</label>
              <select id="home_size" name="home_size" defaultValue="" className={`${inputCls} cursor-pointer`} required {...field('home_size')}>
                <option value="" disabled>Select your home</option>
                {homeSizes.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              {err('home_size')}
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="preferred_date" className="block text-sm font-medium mb-2">Preferred Date <span className="text-foreground-500 font-normal">(optional)</span></label>
              <input id="preferred_date" type="date" name="preferred_date" className={`${inputCls} cursor-pointer`} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="details" className="block text-sm font-medium mb-2">Additional Details <span className="text-foreground-500 font-normal">(optional)</span></label>
              <textarea id="details" name="details" rows={5} maxLength={500} onChange={(ev) => setCount(ev.target.value.length)} className={`${inputCls} h-auto py-3 resize-none`} placeholder="Rooms, pets, special requests…" />
              <div className="flex justify-between">{err('details')}<span className="ml-auto mt-1.5 text-xs text-foreground-500">{count}/500</span></div>
            </div>
            <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
              <button type="submit" disabled={status === 'sending'} className="appearance-none shrink-0 inline-flex items-center justify-center w-full sm:w-auto h-[52px] min-h-[52px] py-4 leading-5 px-8 rounded-full bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 transition-colors">
                {status === 'sending' ? 'Sending…' : 'Request a Quote'}
              </button>
              <div aria-live="polite" className="text-sm">
                {status === 'success' && <p className="text-foreground-950"><i className="ri-checkbox-circle-line text-secondary-500 mr-1" />Thanks — your request was received. We'll be in touch soon.</p>}
                {status === 'error' && <p role="alert" className="text-foreground-950"><i className="ri-error-warning-line text-secondary-500 mr-1" />{formError}</p>}
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}